"use client";

import { useEffect, useId, useState } from "react";

import { usePathname } from "next/navigation";

import { HsaFsa } from "@/components/icons/HsaFsa";
import { InfoIcon } from "@/components/icons/Info";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import TruemedScript from "@/components/vendor/Truemed/TruemedScript";
import {
  HSA_FSA_ELIGIBLE_DUO_PDPS,
  HSA_FSA_ELIGIBLE_PDPS,
  HSA_FSA_TEST_COOKIE,
  HSA_FSA_TEST_VARIANT,
} from "@/constants/hsaFsa";
import type { ProductLine } from "@/types/common";
import { readCookie } from "@/utils/cookies";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

const ELIGIBLE_VALUES = ["eligible", "eligible_with_lmn"];

export function isHsaFsaEligible(value: string | null | undefined): boolean {
  return !!value && ELIGIBLE_VALUES.includes(value);
}

/**
 * Truemed doesn't expose a public JS API to open their modal. Their script binds a click
 * handler to the button it injects with class "truemed-instructions-open" inside our
 * mounted (but visually hidden) #truemed-instructions div. We forward a real click to that
 * button so our own UI can trigger their modal. Undocumented behavior — verify after any
 * Truemed widget update.
 */
function openTruemedModal(wrapperId: string) {
  const trigger = document.querySelector<HTMLElement>(`#${wrapperId} .truemed-instructions-open`);
  if (!trigger) {
    // eslint-disable-next-line no-console
    console.warn("Truemed widget hasn't finished loading yet.");
    return;
  }
  trigger.click();
}

interface TruemedWidgetProps {
  eligibility: string | null | undefined;
  variant?: "pdp" | "cart";
  className?: string;
  /**
   * Only needed for the cart variant — a PDP's own URL already tells `segmentEvent`
   * which product line/business line it's on, but the cart page isn't product-specific,
   * so the "Modal Viewed" event needs these passed in from the specific line item whose
   * badge was clicked.
   */
  productLine?: ProductLine;
  businessLine?: string;
}

/**
 * Renders our own HSA/FSA eligibility UI when the product's `hsa_fsa_eligibility`
 * attribute is "eligible" or "eligible_with_lmn". Truemed's own widget is mounted but
 * visually hidden — clicking our UI forwards a click to their real trigger so their
 * modal still opens, while the visible design is fully ours.
 */
export default function TruemedWidget({
  eligibility,
  variant = "pdp",
  className,
  productLine,
  businessLine,
}: TruemedWidgetProps) {
  const wrapperId = useId().replace(/:/g, "-");
  const pathname = usePathname();
  const normalizedPath = pathname.replace(/\/$/, "");
  const isAllowedPdpPage =
    variant !== "pdp" ||
    HSA_FSA_ELIGIBLE_PDPS.includes(normalizedPath) ||
    HSA_FSA_ELIGIBLE_DUO_PDPS.includes(normalizedPath);
  const [isTestVariant, setIsTestVariant] = useState(false);

  useEffect(() => {
    setIsTestVariant(readCookie(HSA_FSA_TEST_COOKIE) === HSA_FSA_TEST_VARIANT);
  }, []);

  if (!isTestVariant || !isAllowedPdpPage || !isHsaFsaEligible(eligibility)) {
    return null;
  }

  const handleOpen = () => {
    openTruemedModal(wrapperId);
    segmentEvent("Modal Viewed", {
      action: "click",
      content_type: null,
      creative_id: null,
      location: variant === "cart" ? "cart" : "atc area",
      name: "TrueMed Learn More Widget",
      text: variant === "cart" ? "HSA/FSA Eligible" : "Learn more",
      ...(variant === "cart" ? { product_line: productLine, business_line: businessLine } : {}),
    });
  };

  return (
    <div id={wrapperId} className={cn("relative", className)}>
      <div
        className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
        aria-hidden="true"
      >
        <div id="truemed-instructions" {...{ "data-public-id": "tm_qual_gzc4rge62a" }} />
        <TruemedScript />
      </div>

      {variant === "cart" ? (
        <button
          type="button"
          onClick={handleOpen}
          aria-label="HSA/FSA Eligible — Learn more"
          className="inline-flex w-fit items-center gap-0.5 rounded-sm bg-nova-base-solid-20 py-1 px-1.5"
        >
          <TextElementNova
            element="span"
            text="HSA/FSA Eligible"
            className="Caption-10-Dia-Uppercase translate-y-px"
          />
          <InfoIcon className="h-4 w-4 flex-shrink-0" />
        </button>
      ) : (
        <div className="flex w-full items-center gap-2.5 rounded bg-nova-base-solid-10 py-2 px-4 md:px-8 lg:px-4">
          <HsaFsa className="h-4 w-4 flex-shrink-0" />
          <TextElementNova
            element="span"
            text="HSA/FSA eligible."
            className="Body-10-Dia-Strong translate-y-px"
          />
          <button
            type="button"
            onClick={handleOpen}
            aria-label="Learn more"
            className="Caption-10-Dia-Normal ml-auto text-nova-brand-primary-80 underline"
          >
            Learn more
          </button>
        </div>
      )}
    </div>
  );
}
